/** Display formatting, ported from the Phase 0 `app.js` helpers. */

const DASH = '—'

const COMPASS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
]

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** "Sep 15, 8:50 PM" in the viewer's locale and zone. */
export function fmtTime(iso: string | null | undefined): string {
  const d = parse(iso)
  if (!d) return iso ?? ''
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** "8 PM" — the hourly rows only ever span the next 12 hours. */
export function fmtHour(iso: string | null | undefined): string {
  const d = parse(iso)
  if (!d) return iso ?? ''
  return d.toLocaleTimeString([], { hour: 'numeric' })
}

export function compass(deg: number): string {
  return COMPASS[((Math.round(deg / 22.5) % 16) + 16) % 16]
}

export function fmtWind(
  mph: number | null | undefined,
  dir: number | null | undefined,
  gust: number | null | undefined,
): string {
  if (mph == null) return DASH
  const d = dir != null ? `${compass(dir)} ` : ''
  const g = gust != null ? ` (G ${Math.round(gust)})` : ''
  return `${d}${Math.round(mph)} mph${g}`
}

export function fmtTemp(f: number | null | undefined): string {
  return f == null ? DASH : `${Math.round(f)}°F`
}

export function fmtPercent(pct: number | null | undefined): string {
  return pct == null ? DASH : `${Math.round(pct)}%`
}

export function fmtPressure(inhg: number | null | undefined): string {
  return inhg == null ? DASH : `${inhg.toFixed(2)} inHg`
}

export function fmtCoords(lat: number, lon: number): string {
  return `${lat.toFixed(4)}, ${lon.toFixed(4)}`
}

/**
 * The `/api/place` answer as a name that does not overclaim: "Lawrence, KS"
 * in town and "8 mi from Lawrence, KS" out of it, because in open country the
 * nearest named place can be twenty miles off and its name alone is a claim
 * about somewhere else. Under a mile is not worth the words. Empty when NWS
 * names nothing.
 */
export function placeLabel(place: {
  city: string | null
  state: string | null
  distance_mi: number | null
}): string {
  if (!place.city) return ''
  const name = place.state ? `${place.city}, ${place.state}` : place.city
  const miles = place.distance_mi ?? 0
  return miles >= 1 ? `${Math.round(miles)} mi from ${name}` : name
}

/**
 * "just now" / "3 min ago" / "2 h ago" / a date once it is older than a day.
 * `now` is injectable so the tests do not depend on the wall clock.
 */
export function relativeTime(then: Date | null | undefined, now: Date = new Date()): string {
  if (!then || Number.isNaN(then.getTime())) return ''
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000)
  if (seconds < 0) return 'just now'
  if (seconds < 45) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  return fmtTime(then.toISOString())
}
